"use client";

import * as React from "react";
import { CheckIcon, ClipboardIcon, SuccessCheckIcon } from "../icons";
import type { GasEstimate } from "../../types";
import {
  formatGasLimit,
  formatGweiFromWei,
  formatNativeFeeFromWei,
  rewardCardFitLevel,
  shortenTransactionHash,
} from "../../utils";
import { formatRawTokenAmount } from "../../../send/utils";
import type {
  CrossdWithdrawPrepared,
  CrossdWithdrawStatus,
  CrossdWithdrawTarget,
} from "./useCrossdWithdrawFlow";

/** confirm 시점에 준비본이 이보다 오래되면 재-prepare 후 전송한다(스테일 deadline 방지). */
const PREPARED_STALE_MS = 5 * 60 * 1000;

const FALLBACK_TOKEN_ICON =
  "https://contents.crosstoken.io/wallet/token/images/Cross.svg";

interface CrossdWithdrawDialogProps {
  open: boolean;
  status: CrossdWithdrawStatus;
  target: CrossdWithdrawTarget | null;
  /** 토큰 아이콘/심볼 (섹션이 token info API 기준으로 해석해 전달). */
  token0Icon?: string;
  token1Icon?: string;
  token0Symbol?: string;
  token1Symbol?: string;
  /** 가스비 표시용 네이티브 토큰 심볼 (info API 기준, 예: tONE/ONE). */
  nativeSymbol?: string;
  txHash?: `0x${string}`;
  error?: string;
  copied?: boolean;
  hasEstimator: boolean;
  prepareWithdraw: () => Promise<CrossdWithdrawPrepared | null>;
  confirmWithdraw: (prepared: CrossdWithdrawPrepared) => Promise<void>;
  onClose: () => void;
  onCopyHash: () => void;
  trackFunnel?: (step: string) => void;
}

function formatFeeValue(
  estimate: GasEstimate | null | undefined,
  isEstimating: boolean,
  nativeSymbol?: string,
): string {
  if (isEstimating) return "Estimating...";
  if (!estimate) return "Wallet estimate";

  const feePerGas = estimate.maxFeePerGas ?? estimate.gasPrice;
  if (!feePerGas || feePerGas <= 0n || estimate.gasLimit <= 0n) {
    return "Wallet estimate";
  }
  return `${formatNativeFeeFromWei(estimate.gasLimit * feePerGas)} ${nativeSymbol ?? ""}`.trim();
}

function formatGasMetric(
  value: string,
  unit: string,
  isEstimating: boolean,
): string {
  if (isEstimating) return "Estimating...";
  return `${value} ${unit}`.trim();
}

/**
 * CROSSD v3 포지션 전체 출금 확인 다이얼로그. `WithdrawDialog.tsx`의 시트
 * 구조(`wp-claim-*`)를 재사용하지만, 대상 포지션이 이미 확정돼 있고
 * 전량 출금만 지원하므로 토큰 선택/금액 입력이 없다. 열릴 때 1회
 * `prepareWithdraw()`로 시뮬레이션 + 가스 추정을 받아 예상 수령량을 보여주고,
 * Withdraw 클릭 시 준비본이 5분 이상 지났으면 재-prepare 후 전송한다.
 */
export function CrossdWithdrawDialog({
  open,
  status,
  target,
  token0Icon,
  token1Icon,
  token0Symbol,
  token1Symbol,
  nativeSymbol,
  txHash,
  error,
  copied = false,
  hasEstimator,
  prepareWithdraw,
  confirmWithdraw,
  onClose,
  onCopyHash,
  trackFunnel,
}: CrossdWithdrawDialogProps) {
  const [prepared, setPrepared] = React.useState<CrossdWithdrawPrepared | null>(
    null,
  );
  const [preparedAt, setPreparedAt] = React.useState<number | null>(null);
  // open/target 조합이 바뀔 때마다 증가시켜, 닫힌 뒤(또는 재오픈된 뒤) 늦게
  // 도착한 prepare 응답이 새 상태를 덮어쓰지 못하게 막는다.
  const generationRef = React.useRef(0);

  React.useEffect(() => {
    if (!open || !target) return;
    const generation = ++generationRef.current;
    setPrepared(null);
    setPreparedAt(null);
    trackFunnel?.("dialog_opened");
    prepareWithdraw().then((result) => {
      if (generationRef.current !== generation) return;
      setPrepared(result);
      setPreparedAt(result ? Date.now() : null);
    });
    // prepareWithdraw는 target에 종속된 콜백이므로 target 변경 시 함께 재실행된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target]);

  const isSubmitting = status === "submitting";
  const isEstimating =
    status === "estimating" || (status === "confirm" && !prepared);

  const runPrepareAndConfirm = React.useCallback(async () => {
    let next = prepared;
    const isStale = !preparedAt || Date.now() - preparedAt > PREPARED_STALE_MS;

    if (!next || isStale) {
      const generation = ++generationRef.current;
      next = await prepareWithdraw();
      if (generationRef.current !== generation) return;
      setPrepared(next);
      setPreparedAt(next ? Date.now() : null);
    }

    if (!next) return; // prepareWithdraw가 이미 status="error"로 남겨둔다.
    await confirmWithdraw(next);
  }, [prepared, preparedAt, prepareWithdraw, confirmWithdraw]);

  if (!open || !target) return null;

  const { position, detail } = target;
  const token0 = detail.pool?.token0;
  const token1 = detail.pool?.token1;
  const symbol0 =
    token0Symbol || token0?.symbol || position.pool?.token0_symbol || "?";
  const symbol1 =
    token1Symbol || token1?.symbol || position.pool?.token1_symbol || "?";
  const decimals0 = token0?.decimals ?? 18;
  const decimals1 = token1?.decimals ?? 18;
  const icon0 = token0Icon || token0?.logo_url || FALLBACK_TOKEN_ICON;
  const icon1 = token1Icon || token1?.logo_url || FALLBACK_TOKEN_ICON;
  const pairLabel = `${symbol0}/${symbol1}`;

  const expectedAmount0Display = prepared
    ? formatRawTokenAmount(prepared.expectedAmount0.toString(), decimals0)
    : null;
  const expectedAmount1Display = prepared
    ? formatRawTokenAmount(prepared.expectedAmount1.toString(), decimals1)
    : null;

  const priorityFee = formatGweiFromWei(
    prepared?.estimate?.maxPriorityFeePerGas,
  );
  const maxFee = formatGweiFromWei(
    prepared?.estimate?.maxFeePerGas ?? prepared?.estimate?.gasPrice,
  );
  const gasLimit = formatGasLimit(prepared?.estimate?.gasLimit);

  return (
    <div
      className="wp-claim-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) {
          onClose();
        }
      }}
    >
      <section
        className="wp-claim-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wp-crossd-withdraw-title"
        aria-label="Withdraw position"
        data-status={status}
        data-vaul-no-drag
      >
        <div className="wp-claim-grabber" aria-hidden>
          <span />
        </div>

        <div className="wp-claim-content wp-withdraw-content">
          {status === "success" && txHash ? (
            <>
              <span
                className="wp-claim-icon wp-claim-icon--success"
                aria-hidden
              >
                <SuccessCheckIcon />
              </span>

              <div className="wp-claim-copy">
                <h3 id="wp-crossd-withdraw-title" className="wp-claim-title">
                  Withdraw Submitted!
                </h3>
                <p className="wp-claim-desc">
                  Your {pairLabel} position withdrawal has been submitted.
                  Balances will update shortly after the transaction settles.
                </p>
              </div>

              <div className="wp-claim-list" aria-label="Withdraw transaction">
                <div className="wp-claim-list-item">
                  <span>Tx Hash</span>
                  <button
                    type="button"
                    className="wp-claim-hash"
                    onClick={onCopyHash}
                    aria-label="Copy transaction hash"
                    data-track="crossd_withdraw_hash_copy"
                  >
                    <strong>{shortenTransactionHash(txHash)}</strong>
                    {copied ? <CheckIcon /> : <ClipboardIcon />}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {status === "error" && (
                <div className="wp-claim-heading">
                  <h3 id="wp-crossd-withdraw-title" className="wp-claim-title">
                    Withdraw Failed
                  </h3>
                  <p className="wp-claim-error-message">
                    {error || "The withdraw request could not be completed."}
                  </p>
                </div>
              )}

              <div
                className="wp-claim-reward-card"
                data-fit={rewardCardFitLevel(
                  symbol0,
                  expectedAmount0Display ?? "",
                )}
                aria-label={`Expected ${symbol0}`}
              >
                <span className="wp-claim-reward-token" aria-hidden>
                  <img src={icon0} alt="" width={28} height={28} />
                </span>
                <strong className="wp-claim-reward-symbol">{symbol0}</strong>
                <strong className="wp-claim-reward-amount">
                  {expectedAmount0Display ?? "Estimating..."}
                </strong>
              </div>

              <div
                className="wp-claim-reward-card"
                data-fit={rewardCardFitLevel(
                  symbol1,
                  expectedAmount1Display ?? "",
                )}
                aria-label={`Expected ${symbol1}`}
              >
                <span className="wp-claim-reward-token" aria-hidden>
                  <img src={icon1} alt="" width={28} height={28} />
                </span>
                <strong className="wp-claim-reward-symbol">{symbol1}</strong>
                <strong className="wp-claim-reward-amount">
                  {expectedAmount1Display ?? "Estimating..."}
                </strong>
              </div>

              {hasEstimator && (
                <div
                  className="wp-claim-account-card"
                  aria-label="Withdraw gas fee"
                >
                  <div className="wp-claim-detail-list">
                    <div className="wp-claim-detail-row">
                      <span>Est. Tx Fee</span>
                      <strong>
                        {formatFeeValue(
                          prepared?.estimate,
                          isEstimating,
                          nativeSymbol,
                        )}
                      </strong>
                    </div>
                    <div className="wp-claim-detail-row wp-claim-detail-row--sub">
                      <span>Priority Fee Per Gas</span>
                      <strong>
                        {formatGasMetric(priorityFee, "Gwei", isEstimating)}
                      </strong>
                    </div>
                    <div className="wp-claim-detail-row wp-claim-detail-row--sub">
                      <span>Max Fee Per Gas</span>
                      <strong>
                        {formatGasMetric(maxFee, "Gwei", isEstimating)}
                      </strong>
                    </div>
                    <div className="wp-claim-detail-row wp-claim-detail-row--sub">
                      <span>Gas Limit</span>
                      <strong>
                        {formatGasMetric(gasLimit, "", isEstimating)}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          <div
            className="wp-claim-actions"
            data-count={status === "confirm" || status === "error" ? 2 : 1}
          >
            {(status === "confirm" || status === "error") && (
              <button
                type="button"
                className="wp-claim-secondary"
                data-track="crossd_withdraw_cancel"
                onClick={onClose}
              >
                {status === "error" ? "Close" : "Cancel"}
              </button>
            )}
            <button
              type="button"
              className="wp-claim-primary"
              data-track="crossd_withdraw_confirm"
              onClick={() => {
                if (status === "success") {
                  onClose();
                  return;
                }
                if (isSubmitting || isEstimating) return;
                trackFunnel?.("confirm_clicked");
                void runPrepareAndConfirm();
              }}
              disabled={isSubmitting || isEstimating}
            >
              {status === "success" ? (
                "Done"
              ) : isSubmitting ? (
                <span
                  className="wp-claim-button-spinner"
                  aria-label="Waiting"
                />
              ) : isEstimating ? (
                <span
                  className="wp-claim-button-spinner"
                  aria-label="Estimating"
                />
              ) : status === "error" ? (
                "Retry"
              ) : (
                "Withdraw"
              )}
            </button>
          </div>
        </div>

        <div className="wp-claim-home-indicator" aria-hidden>
          <span />
        </div>
      </section>
    </div>
  );
}
